import React from "react";
import { exportTableData } from "../../../../../utils/commonFunction";
import {
    StyledExportButton,
    StyledExportGroup,
} from "../../../../library/BulkToolbarButtons";
import { parsePrerequisiteIds, resolveNetsuiteProductName } from "../../../../../utils/VersionModulesUtils";

const HEADERS = ["Portal Name", "Code", "Cr", "Study Path", "Term", "Year", "Programmes", "NetSuite Product", "Pre-Requisites"];

/**
 * Formats the currently-filtered (not just the current page's) module rows
 * into export-ready objects, resolving the same NetSuite product name and
 * prerequisite names the table cells show.
 */
function buildRows({ rows, netsuiteList, moduleNameById }) {
    return rows.map((row) => {
        const prerequisiteNames = parsePrerequisiteIds(row)
            .map((id) => moduleNameById.get(id))
            .filter(Boolean);

        return {
            "Portal Name": row.Portal || "-",
            Code: row.Code || "-",
            Cr: row.credits ?? "-",
            "Study Path": row.studyPath || "No Study Path",
            Term: row.term ?? "-",
            Year: row.year ?? "-",
            Programmes: row.Programmes ?? 0,
            "NetSuite Product": resolveNetsuiteProductName(netsuiteList, row.netsuite_product),
            "Pre-Requisites": prerequisiteNames.length ? prerequisiteNames.join(", ") : "-",
        };
    });
}

/**
 * Export buttons (Excel, CSV, PDF) for the Version Modules table - reuses the
 * shared `exportTableData` download helper (also used by the sibling PMC
 * Study Path/AvailableTables exports) instead of re-implementing CSV/XLSX/PDF
 * downloads, and exports every module matching the current search/filters,
 * not only the visible page, matching the pre-redesign `AssignedTable.js`'s
 * export behaviour. Disabled whenever there's nothing reliable to export:
 * still loading, failed to load, empty, mid bulk-unlink, or before NetSuite
 * product names have resolved (otherwise exported rows would show "-" for
 * NetSuite Product).
 * @param {object} hook State from `useVersionModules`.
 */
function VersionModulesExport({ hook }) {
    const isDisabled = hook.loading || hook.isError || hook.totalRows === 0 || hook.isProcessing || hook.isNetsuiteLoading;

    const disabledReason = hook.isProcessing
        ? "A bulk action is currently running"
        : hook.isNetsuiteLoading
            ? "NetSuite product data is still loading"
            : hook.loading
                ? "Modules are loading"
                : hook.isError
                    ? "Modules couldn't be loaded"
                    : hook.totalRows === 0
                        ? "There are no modules to export"
                        : undefined;

    const exportData = (fileType) => {
        const formattedRows = buildRows({
            rows: hook.allFilteredRows,
            netsuiteList: hook.netsuiteList,
            moduleNameById: hook.moduleNameById,
        });

        exportTableData(fileType, "PMC_Version_Modules", HEADERS, formattedRows);
    };

    return (
        <StyledExportGroup aria-label="Export modules">
            <StyledExportButton
                type="button"
                title={isDisabled ? disabledReason : "Export Excel"}
                aria-label="Export Excel"
                disabled={isDisabled}
                onClick={() => exportData("xlsx")}
            >
                <i className="fal fa-file-excel" aria-hidden="true" />
            </StyledExportButton>

            <StyledExportButton
                type="button"
                title={isDisabled ? disabledReason : "Export CSV"}
                aria-label="Export CSV"
                disabled={isDisabled}
                onClick={() => exportData("csv")}
            >
                <i className="fal fa-file-csv" aria-hidden="true" />
            </StyledExportButton>

            <StyledExportButton
                type="button"
                title={isDisabled ? disabledReason : "Export PDF"}
                aria-label="Export PDF"
                disabled={isDisabled}
                onClick={() => exportData("pdf")}
            >
                <i className="fal fa-file-pdf" aria-hidden="true" />
            </StyledExportButton>
        </StyledExportGroup>
    );
}

export default VersionModulesExport;
